import { useState } from 'react';
import { ConfirmModal } from '../../../components/ConfirmModal';
import {
  AdminInviteError,
  resendSetupInvitation,
  revokeSetupInvitation,
} from '../../../services/adminInvites';
import type { SetupInvitation } from '../../../types/adminInvites';
import { formatUserDate } from '../../settings/userDisplay';
import { formatAnnualPrice, sportLabel } from '../../signup/signupFormat';
import { AdminPanel } from '../AdminPage';
import { commonMessage } from './inviteForm';
import '../../../components/DataTable.css';
import '../../settings/PendingInvitations.css';
import './PendingSetupInvitations.css';

interface PendingSetupInvitationsProps {
  list: SetupInvitation[];
  error: string | null;
  reload: () => Promise<void>;
}

interface Notice {
  /** The row's id: two rows can be working at once, and one must not overwrite the other's line. */
  key: string;
  tone: 'success' | 'error';
  text: string;
}

const dateOrUnknown = (iso: string | null): string => (iso ? formatUserDate(iso) : 'Unknown');

const paymentText = (row: SetupInvitation): string => {
  if (!row.requiresPayment) return 'Free';
  if (row.effectiveAnnualPriceCents === null) return 'Required';
  const price = formatAnnualPrice(row.effectiveAnnualPriceCents, 'usd');
  return row.customAnnualPriceCents !== null ? `${price} (custom)` : price;
};

/**
 * Setup invitations that have been sent and not used: pending and expired rows, newest first.
 * The table only renders when there are rows; the live regions stay mounted so the result of
 * revoking the last row is still announced.
 */
export const PendingSetupInvitations = ({ list, error, reload }: PendingSetupInvitationsProps) => {
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());
  const [notices, setNotices] = useState<readonly Notice[]>([]);
  const [confirming, setConfirming] = useState<SetupInvitation | null>(null);

  const markBusy = (id: string, busy: boolean) =>
    setBusyIds((current) => {
      const next = new Set(current);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });

  const say = (key: string, tone: Notice['tone'], text: string) =>
    setNotices((current) => [...current.filter((n) => n.key !== key), { key, tone, text }]);

  const clear = (key: string) => setNotices((current) => current.filter((n) => n.key !== key));

  const failureOf = (err: unknown) =>
    err instanceof AdminInviteError ? err : new AdminInviteError('failed', '');

  const resend = async (row: SetupInvitation) => {
    markBusy(row.id, true);
    clear(row.id);

    try {
      await resendSetupInvitation(row.id);
      say(row.id, 'success', `Sent to ${row.email} again. The old link no longer works.`);
      await reload();
    } catch (err: unknown) {
      const failure = failureOf(err);
      const text =
        // not_pending while the invitee is mid-accept: the server's message says so, and is truer
        // than "no longer pending".
        failure.code === 'not_pending' && failure.message
          ? failure.message
          : failure.kind === 'not_found' || failure.kind === 'conflict'
          ? `The invitation for ${row.email} is no longer pending. The list has been refreshed.`
          : failure.kind === 'send_failed'
            ? `The email to ${row.email} did not go out. Try again in a moment.`
            : commonMessage(failure.kind, failure.message);
      say(row.id, 'error', text);
      if (failure.kind !== 'throttled' && failure.kind !== 'no_transport') await reload();
    } finally {
      markBusy(row.id, false);
    }
  };

  const revoke = async (row: SetupInvitation) => {
    markBusy(row.id, true);
    clear(row.id);

    try {
      await revokeSetupInvitation(row.id);
      say(row.id, 'success', `The invitation for ${row.email} was revoked. Its link no longer works.`);
    } catch (err: unknown) {
      const failure = failureOf(err);
      say(
        row.id,
        'error',
        // not_pending while the invitee is mid-accept: the server's message says so, and is truer
        // than "no longer pending".
        failure.code === 'not_pending' && failure.message
          ? failure.message
          : failure.kind === 'not_found' || failure.kind === 'conflict'
          ? `The invitation for ${row.email} is no longer pending. The list has been refreshed.`
          : commonMessage(failure.kind, failure.message)
      );
    } finally {
      setConfirming(null);
      markBusy(row.id, false);
      await reload();
    }
  };

  const successes = notices.filter((n) => n.tone === 'success');
  const errors = notices.filter((n) => n.tone === 'error');

  return (
    <>
      <div role="status" aria-atomic="false" data-testid="setup-invitations-status">
        {successes.map((n) => (
          <p key={n.key} className="setup-invitations__notice">
            {n.text}
          </p>
        ))}
      </div>
      {(errors.length > 0 || error) && (
        <div
          role="alert"
          aria-atomic="false"
          className="setup-invitations__alerts"
          data-testid="setup-invitations-alert"
        >
          {error && <p className="admin-error">{error}</p>}
          {errors.map((n) => (
            <p key={n.key} className="admin-error">
              {n.text}
            </p>
          ))}
        </div>
      )}

      {list.length > 0 && (
        <AdminPanel
          title="Pending setup invitations"
          titleId="pending-setup-invitations-title"
          description="New programs invited but not created yet."
          data-testid="pending-setup-invitations"
        >

          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Email</th>
                  <th scope="col">Program</th>
                  <th scope="col">Sport</th>
                  <th scope="col">Payment</th>
                  <th scope="col">Status</th>
                  <th scope="col">Sent</th>
                  <th scope="col" className="data-table__actions">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((row) => {
                  const busy = busyIds.has(row.id);
                  return (
                    <tr key={row.id}>
                      <td>
                        <span className="pending-invitations__email">{row.email}</span>{' '}
                        {row.lastSendSucceeded === false && (
                          <span className="data-table__tag pending-invitations__tag--warning">
                            Not delivered
                          </span>
                        )}
                      </td>
                      <td>
                        {row.programName ?? 'Not set'}
                        {row.schoolName && (
                          <div className="data-table__muted">{row.schoolName}</div>
                        )}
                      </td>
                      <td className="data-table__muted">{sportLabel(row.sport) || 'Not set'}</td>
                      <td className="data-table__muted">{paymentText(row)}</td>
                      <td>
                        <span
                          className={`data-table__badge${
                            row.status === 'Pending' ? ' data-table__badge--primary' : ''
                          }`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className="data-table__muted">
                        {dateOrUnknown(row.lastSentAt ?? row.createdAt)}
                        {row.sentCount !== null && row.sentCount > 1
                          ? ` (sent ${row.sentCount} times)`
                          : ''}
                      </td>
                      <td className="data-table__actions">
                        {/*
                          aria-disabled rather than disabled, so a keyboard user's focus is not
                          dropped to <body> mid-action. Named per row for screen readers.
                        */}
                        <div className="pending-invitations__actions">
                          <button
                            type="button"
                            className="pending-invitations__action"
                            aria-disabled={busy}
                            aria-busy={busy}
                            aria-label={`Resend the setup invitation to ${row.email}`}
                            data-testid={`setup-resend-${row.id}`}
                            onClick={() => {
                              if (!busy) void resend(row);
                            }}
                          >
                            Resend
                          </button>
                          <button
                            type="button"
                            className="pending-invitations__action pending-invitations__action--danger"
                            aria-disabled={busy}
                            aria-busy={busy}
                            aria-label={`Revoke the setup invitation to ${row.email}`}
                            data-testid={`setup-revoke-${row.id}`}
                            onClick={() => {
                              if (!busy) setConfirming(row);
                            }}
                          >
                            Revoke
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AdminPanel>
      )}

      <ConfirmModal
        isOpen={confirming !== null}
        onClose={() => setConfirming(null)}
        onConfirm={() => {
          if (confirming && !busyIds.has(confirming.id)) void revoke(confirming);
        }}
        title="Revoke setup invitation?"
        message={
          confirming ? `The link sent to ${confirming.email} stops working right away.` : ''
        }
        confirmText="Revoke"
        busy={confirming !== null && busyIds.has(confirming.id)}
      />
    </>
  );
};
